import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useStockData from "../hooks/useStockData";
import AddProductModal from "../components/AddProductModal";
import EditProductModal from "../components/EditProductModal";
import ProductList from "../components/ProductList";
import { getUserRole } from "../utils/Auth";
import DeleteProductModal from "../components/DeleteProductModal";
import { useTheme } from "../context/ThemeContext";
import useAgentData from "../hooks/useAgentData";

const CategoryProductsPage = () => {
  const userRole = getUserRole();
  const [showProductActions, setShowProductActions] = useState(false);
  const { categoryId } = useParams();
  const {
    fetchProductsByCategory,
    selectedCategoryName,
    products,
    addProduct,
    updateProduct,
    deleteProduct,
  } = useStockData();

  const [successMessage, setSuccessMessage] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const { darkMode } = useTheme();
  const [isMarmuristPrice, setIsMarmuristPrice] = useState(false);
  const { canAccessMarmurist } = useAgentData();

  const [newProduct, setNewProduct] = useState({
    name: "",
    price: "",
    priceMarmurist: "",
    availableStock: "",
    unitType: "MP",
  });

  useEffect(() => {
    if (categoryId) {
      fetchProductsByCategory(categoryId);
    }
  }, [categoryId, fetchProductsByCategory]);

  const toggleProductActions = () => {
    setShowProductActions(!showProductActions);
  };

  const handleAddProduct = async () => {
    if (
      !newProduct.name ||
      !newProduct.price ||
      !newProduct.priceMarmurist ||
      !newProduct.availableStock ||
      !newProduct.unitType
    ) {
      alert("Toate câmpurile sunt obligatorii!");
      return;
    }

    const formData = new FormData();
    formData.append("Name", newProduct.name);
    formData.append("Price", parseFloat(newProduct.price));
    formData.append("PriceMarmurist", parseFloat(newProduct.priceMarmurist));
    formData.append("AvailableStock", parseInt(newProduct.availableStock));
    formData.append("CategoryId", categoryId);
    formData.append("UnitType", newProduct.unitType);
    formData.append("CostPrice", parseFloat(newProduct.costPrice) || 0);

    if (newProduct.imageFile) {
      formData.append("ImageFile", newProduct.imageFile);
    }

    await addProduct(formData); // 🔥 FormData küldése az `addProduct` függvénynek

    // ✅ 1 másodperces várakozás után állítsa vissza az állapotokat
    setTimeout(() => {
      setIsAdding(false);
      setPreviewImage(null);
      setNewProduct({
        name: "",
        price: "",
        priceMarmurist: "",
        availableStock: "",
        unitType: "MP",
        imageFile: null,
      });
    }, 1000); // ⏳ 1000 ms = 1 másodperc
  };

  // 🔥 Szerkesztés elindítása
  const handleEdit = (product) => {
    setEditingProduct(product);
    setPreviewImage(null); // 🔥 Előnézet törlése, ha szerkesztünk egy meglévő terméket
    setIsEditing(true);
  };

  const handleUpdate = async () => {
    if (editingProduct) {
      await updateProduct(editingProduct.id, editingProduct);

      // ✅ Megjeleníti a sikerüzenetet
      setSuccessMessage("✅ Produsul a fost modificat cu succes!");

      // 🔥 Késleltetett modal bezárás
      setTimeout(() => {
        setSuccessMessage(""); // Üzenet törlése
        setIsEditing(false); // Modal bezárása **2 másodperc után**
        setEditingProduct(null); // Termék resetelése
        setPreviewImage(null); // Kép előnézet törlése
      }, 2000);
    }
  };

  const handleDelete = (productId, productName) => {
    setProductToDelete({ id: productId, name: productName }); // 🔥 Csak az ID-t mentjük el
    setIsDeleteModalOpen(true); // 🔥 Megnyitjuk a törlési modalt
  };

  const confirmDelete = async (productId) => {
    await deleteProduct(productId);
    setIsDeleteModalOpen(false);
    setProductToDelete(null);
  };

  return (
    <div className="p-6">
      <h2
        className={`text-5xl font-semibold mb-4 text-center ${darkMode ? "text-white" : "text-black"
          }`}
      >
        Produse - {selectedCategoryName}
      </h2>

      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        {userRole === "Admin" && (
          <div className="flex gap-4">
            <button
              onClick={toggleProductActions}
              className="px-4 py-4 bg-blue-600 text-white rounded-3xl"
            >
              {showProductActions ? "Ascunde" : "Adaugă/Modifică Produs"}
            </button>

            {showProductActions && (
              <button
                onClick={() => setIsAdding(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-3xl"
              >
                + Adaugă Produs
              </button>
            )}
          </div>
        )}

        {canAccessMarmurist && (
          <button
            onClick={() => setIsMarmuristPrice(!isMarmuristPrice)}
            className="px-4 py-4 bg-gray-600 text-white rounded-3xl"
          >
            {isMarmuristPrice
              ? "Schimbă la prețuri finale"
              : "Schimbă la prețuri pentru marmuriști"}
          </button>
        )}
      </div>

      <ProductList
        products={products.map((product) => ({
          ...product,
          displayedPrice: isMarmuristPrice
            ? product.priceMarmurist
            : product.price,
        }))}
        handleEdit={
          showProductActions
            ? (product) => setEditingProduct(product) || setIsEditing(true)
            : null
        }
        handleDelete={showProductActions ? handleDelete : null}
        showProductActions={showProductActions}
      />

      {userRole === "Admin" && (
        <>
          <AddProductModal
            isAdding={isAdding}
            setIsAdding={setIsAdding}
            newProduct={newProduct}
            setNewProduct={setNewProduct}
            handleAddProduct={handleAddProduct} // ✅ Meghívja a helyes függvényt!
            previewImage={previewImage}
            setPreviewImage={setPreviewImage}
          />

          <EditProductModal
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            editingProduct={editingProduct}
            setEditingProduct={setEditingProduct}
            handleUpdate={async () => {
              await updateProduct(editingProduct.id, editingProduct);
              setIsEditing(false);
              setEditingProduct(null);
              setPreviewImage(null);
            }}
            previewImage={previewImage}
            setPreviewImage={setPreviewImage}
            isMarmuristPrice={isMarmuristPrice} // 🔥 ÚJ: ezt adjuk át!
          />

          {/* 🔥 ÚJ DELETE MODAL */}
          <DeleteProductModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={confirmDelete}
            product={productToDelete}
          />
        </>
      )}
    </div>
  );
};

export default CategoryProductsPage;
